/**
 * Design-system theme for the public dialogs.
 *
 * Both `loginModal.js` and `BankIdAuthModal.js` had grown their own copy of the
 * same eight colour constants — a slate/emerald palette (`#00875A`, `#00C281`,
 * `#0f172a`, `#e2e8f0`) that belongs to no part of Klargo. It is the palette
 * the landing page itself was ported away from, so pressing any button on the
 * public page opened a dialog in the pre-port colours.
 *
 * The tokens below are the DS v4.7 values, read from `Home.css`'s `:root`.
 * They are duplicated here as literals rather than referenced as CSS variables
 * because MUI renders these dialogs into a portal and computes `sx` in JS; the
 * two must be kept in step, so they carry the token name they mirror.
 *
 * Klargo's dialog grammar, from Klargo-Reference/src/components/auth/LoginDialog:
 *   - serif ink title, sans body
 *   - ONE filled action, ink not green; everything else is a hairline outline
 *   - 44px controls at 9px radius
 *   - the sheet is a 13px card with a hairline edge and ONE soft shadow
 *   - green appears only as the focus ring and as a state mark, never as a fill
 */

/* --- Tokens (mirroring Home.css :root) ----------------------------------- */
export const DS = {
  canvas: "#f7faf8",
  sheet: "#ffffff",
  pageZone: "#f0f5f2",

  ink: "#14242e",
  inkHover: "#1d3444",
  inkOn: "#f7f9f9",
  body: "#24323c",
  text: "#44525c",
  muted: "#55616b",
  mutedSoft: "#64717b",

  green: "#0b7a55",
  greenDeep: "#0a5c41",
  greenBg: "#e1efe7",

  ruleRow: "rgba(20, 36, 46, 0.09)",
  ruleEdge: "rgba(20, 36, 46, 0.16)",
  ruleControl: "rgba(20, 36, 46, 0.55)",
  fieldBorder: "rgba(20, 36, 46, 0.34)",
  fieldBorderHover: "rgba(20, 36, 46, 0.52)",
  fieldBg: "#ffffff",
  fieldBgDisabled: "#eef0f0",
  fieldBgLocked: "#f8f9f9",

  red: "#b3261e",
  redFg: "#8f2018",
  redBg: "#f8e9e7",

  serif: '"Source Serif 4", Georgia, "Times New Roman", serif',
  sans: '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, Menlo, monospace',
};

/* --- The dialog card ------------------------------------------------------ */
/** One of the only two places the public surface spends a shadow. 13px radius
 *  and a hairline edge: the card is a sheet, and edges here are drawn. */
export const modalCardSx = {
  width: "100%",
  maxWidth: 480,
  backgroundColor: DS.sheet,
  border: `1px solid ${DS.ruleEdge}`,
  borderRadius: "13px",
  boxShadow: "0 8px 32px rgba(16, 30, 40, 0.16)",
};

export const modalTitleSx = {
  fontFamily: DS.serif,
  fontSize: "26px",
  fontWeight: 700,
  lineHeight: 1.18,
  letterSpacing: "-0.01em",
  color: DS.ink,
};

export const modalLedeSx = {
  fontFamily: DS.sans,
  fontSize: "15px",
  lineHeight: 1.55,
  color: DS.text,
};

/* --- Actions -------------------------------------------------------------- */
const actionBase = {
  fontFamily: DS.sans,
  fontSize: "15px",
  fontWeight: 600,
  letterSpacing: 0,
  textTransform: "none",
  minHeight: 44,
  borderRadius: "9px",
  boxShadow: "none",
};

/** The dialog's ONE filled action. Ink, never green: green is the accent that
 *  marks state, and a green button competes with the verified marks. */
export const inkButtonSx = {
  ...actionBase,
  backgroundColor: DS.ink,
  color: DS.inkOn,
  border: 0,
  "&:hover": { backgroundColor: DS.inkHover, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "rgba(20, 36, 46, 0.38)", color: DS.inkOn },
};

export const outlineButtonSx = {
  ...actionBase,
  backgroundColor: "transparent",
  color: DS.ink,
  border: `1px solid ${DS.ruleControl}`,
  "&:hover": {
    backgroundColor: "rgba(20, 36, 46, 0.05)",
    borderColor: DS.ruleControl,
  },
};

/** Tertiary — a text action, the same weight as the "need help" link in the
 *  reference dialog. Never a third button. */
export const textButtonSx = {
  fontFamily: DS.sans,
  fontSize: "13px",
  fontWeight: 500,
  textTransform: "none",
  color: DS.muted,
  minHeight: 40,
  "&:hover": { backgroundColor: "transparent", color: DS.ink, textDecoration: "underline" },
  textUnderlineOffset: "4px",
};

/* --- Fields --------------------------------------------------------------- */
/** Focus is the ONLY place green appears in these dialogs: an ink border with
 *  a soft green ring, the same focus grammar the rest of the DS uses. */
export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "9px",
    backgroundColor: DS.sheet,
    fontFamily: DS.sans,
    fontSize: "15px",
    color: DS.ink,
    "& fieldset": { borderColor: DS.fieldBorder },
    "&:hover fieldset": { borderColor: DS.fieldBorderHover },
    "&.Mui-focused fieldset": { borderColor: DS.ink, borderWidth: "1px" },
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(11, 122, 85, 0.16)" },
    // A field error is a red border, never a panel — the same grammar the
    // reference's .ds-input[aria-invalid] rule uses.
    "&.Mui-error fieldset": { borderColor: DS.red },
    "&.Mui-error:hover fieldset": { borderColor: DS.red },
  },
  "& .MuiInputBase-input::placeholder": { color: DS.mutedSoft, opacity: 1 },
};

/** Verified/locked data (BankID-sourced names, the personal identity number):
 *  readable ink text on a barely-tinted ground, clearly not a live control.
 *  Disabled MUI fields grey their own text by default, which read as "we lost
 *  this value" rather than "this value is confirmed" — the ink colour here is
 *  what tells those apart. */
export const lockedFieldSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    "&.Mui-disabled": {
      backgroundColor: DS.fieldBgLocked,
      "& fieldset": { borderColor: "rgba(20, 36, 46, 0.14)" },
    },
    "&.Mui-disabled .MuiInputBase-input": {
      WebkitTextFillColor: DS.ink,
      color: DS.ink,
    },
  },
};

/** Technical identifiers — registration plates, account numbers, personal
 *  identity numbers — take the mono voice, per the reference's own
 *  `.ds-input--mono` convention. */
export const monoFieldSx = {
  "& .MuiInputBase-input": { fontFamily: DS.mono, letterSpacing: "0.02em" },
};

export const fieldLabelSx = {
  fontFamily: DS.sans,
  fontSize: "14px",
  fontWeight: 500,
  color: DS.ink,
  marginBottom: "6px",
  textAlign: "left",
};

/* --- Tab strip ------------------------------------------------------------ */
export const tabStripSx = {
  backgroundColor: DS.pageZone,
  borderRadius: "10px",
  padding: "4px",
};

export const tabSx = (active) => ({
  fontFamily: DS.sans,
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 500,
  borderRadius: "7px",
  minHeight: 36,
  padding: "6px 12px",
  backgroundColor: active ? DS.sheet : "transparent",
  color: active ? DS.ink : DS.muted,
  boxShadow: active ? "0 1px 2px rgba(16, 30, 40, 0.06)" : "none",
});

/* --- The quiet legal footer ---------------------------------------------- */
export const legalSx = {
  display: "block",
  marginTop: "12px",
  paddingTop: "12px",
  borderTop: `1px solid ${DS.ruleRow}`,
  fontFamily: DS.sans,
  fontSize: "11px",
  lineHeight: 1.6,
  textAlign: "center",
  color: DS.muted,
};

export const legalLinkStyle = {
  color: DS.ink,
  fontWeight: 500,
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  cursor: "pointer",
};

/* --- Type roles for focused auth/utility pages ----------------------------
 * §32 of the reference DS: surfaces that sit before or beside the
 * authenticated shells (account setup, document verification) keep the
 * Klargo identity but carry no navigation. These mirror the reference's
 * .ds-title-section / .ds-title-component / .ds-body / .ds-label roles.
 */
export const pageTitleSx = {
  fontFamily: DS.serif,
  fontWeight: 700,
  fontSize: "22px",
  lineHeight: 1.2,
  letterSpacing: "-0.01em",
  color: DS.ink,
};

export const pageLedeSx = {
  fontSize: "15px",
  fontWeight: 400,
  lineHeight: 1.55,
  color: DS.text,
};

export const sectionHeadingSx = {
  fontFamily: DS.serif,
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: 1.25,
  color: DS.ink,
};

export const fieldErrorTextSx = {
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: 1.4,
  color: DS.red,
};

export const fieldHelperTextSx = {
  fontSize: "12.5px",
  fontWeight: 400,
  lineHeight: 1.5,
  color: DS.muted,
};

/** The only sanctioned "info panel" inside a form — reserved for genuine
 *  block-level notes, never for a single field's error. */
export const panelNoteSx = {
  backgroundColor: "rgba(20, 36, 46, 0.03)",
  border: "1px solid rgba(20, 36, 46, 0.08)",
  borderRadius: "10px",
  padding: "14px 16px",
};

export const dividerSx = { borderColor: DS.ruleRow };

/** Segmented yes/no choice: selected is a green contour, never a fill — fills
 *  stay reserved for actions (ink), per the DS action grammar. The border
 *  widens by 1px on selection and the padding steps down to match, so the
 *  control's outer box never shifts when the state changes. */
export const choiceButtonSx = (selected) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: 44,
  padding: selected ? "9px 13px" : "10px 14px",
  fontFamily: DS.sans,
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "9px",
  color: selected ? DS.ink : DS.body,
  backgroundColor: selected ? "rgba(11, 122, 85, 0.05)" : DS.fieldBg,
  border: selected ? `2px solid ${DS.green}` : `1px solid ${DS.fieldBorder}`,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: selected ? "rgba(11, 122, 85, 0.05)" : "rgba(20, 36, 46, 0.03)",
    borderColor: selected ? DS.green : DS.fieldBorderHover,
  },
});

/** The public surface's one entrance move: a 6px rise and a fade, played once
 *  on mount. Reduced motion renders the settled state immediately. */
export const pageEnterSx = {
  "@keyframes cpPageIn": {
    from: { opacity: 0, transform: "translateY(6px)" },
    to: { opacity: 1, transform: "none" },
  },
  animation: "cpPageIn 240ms cubic-bezier(0.22, 1, 0.36, 1)",
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};
